<script lang="ts">
  import { slide } from 'svelte/transition'
  import { onMount } from 'svelte'
  import '../../gettext'

  let mounted = false
  let expanded = true

  const toggle = () => {
    expanded = !expanded
    localStorage.setItem('donation-header-expanded', expanded ? 'true' : 'false')
  }

  onMount(() => {
    if (localStorage.getItem('donation-header-expanded') === 'false') {
      expanded = false
    }

    /* Note: We use a timeout here because tick() doesn't provide enough of a delay to
     * prevent transitions from playing on initial render.
     */
    setTimeout(() => (mounted = true), 10)
  })
</script>

<div class="donation-header">
  <div class="content" class:expanded class:mounted>
    <button
      class="collapse"
      class:expanded
      on:click={toggle}
      aria-label={expanded ? window.gettext('Collapse') : window.gettext('Expand')}
    >
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <mask id="mask0_1_27" style="mask-type:alpha" maskUnits="userSpaceOnUse" x="0" y="0" width="24" height="24">
          <rect x="24" width="24" height="24" transform="rotate(90 24 0)" fill="#D9D9D9" />
        </mask>
        <g mask="url(#mask0_1_27)">
          <path
            d="M12 12.6L15.9 8.7C16.0833 8.51667 16.3166 8.425 16.6 8.425C16.8833 8.425 17.1166 8.51667 17.3 8.7C17.4833 8.88333 17.575 9.11667 17.575 9.4C17.575 9.68333 17.4833 9.91667 17.3 10.1L12.7 14.7C12.6 14.8 12.4916 14.8708 12.375 14.9125C12.2583 14.9542 12.1333 14.975 12 14.975C11.8666 14.975 11.7416 14.9542 11.625 14.9125C11.5083 14.8708 11.4 14.8 11.3 14.7L6.69995 10.1C6.51662 9.91667 6.42495 9.68333 6.42495 9.4C6.42495 9.11667 6.51662 8.88333 6.69995 8.7C6.88328 8.51667 7.11662 8.425 7.39995 8.425C7.68328 8.425 7.91662 8.51667 8.09995 8.7L12 12.6Z"
            fill="white"
          />
        </g>
      </svg>
    </button>
    <div class="message" aria-live="polite">
      {#if expanded}
        <div out:slide={{ duration: mounted ? 500 : 0 }} in:slide={{ duration: mounted ? 500 : 0 }}>
          <p>
            <strong>{window.gettext('We need your support!')}</strong>
            {window.gettext(
              'We are dedicated to providing you the best free online mapping and analysis platform. If every one of the 55000+ members of Data Basin donated just $10, we would be able to do what so many of you have asked us to do:',
            )}
          </p>
          <ul>
            <li>{window.gettext('Build new tools such as enhanced visualization and graphing & analytical tools')}</li>
            <li>{window.gettext('Upload new datasets and update current datasets')}</li>
            <li>{window.gettext('Provide more training and workshop opportunities')}</li>
          </ul>
          <p>
            {@html window.interpolate(
              window.gettext(
                'We encourage you to make a one-time donation or become an on-going sponsor. The %(linkOpen)sConservation Biology Institute%(linkClose)s (CBI) is a 501(c)(3) organization. All gifts are tax deductible. You can be confident your donations to CBI will be spent in support of our mission. Please feel free to contact us with any questions!',
              ),
              {
                linkOpen: '<a href="https://consbio.org" target="_blank" rel="noopener noreferrer">',
                linkClose: '</a>',
              },
              true,
            )}
          </p>
          <div class="buttons">
            <a
              class="donate"
              on:click={toggle}
              href="https://donorbox.org/data-basin"
              target="_blank"
              rel="noopener noreferrer"
            >
              {window.gettext('Donate Today')}
            </a>
            <button class="hide" on:click={toggle}>{window.gettext('Hide message')}</button>
          </div>
        </div>
      {:else}
        <div class="collapsed">
          <button class="support" on:click={toggle}>{window.gettext('We need your support!')}</button>
          <a class="donate" href="https://donorbox.org/data-basin" target="_blank" rel="noopener noreferrer">
            {window.gettext('Donate Today')}
          </a>
        </div>
      {/if}
    </div>
  </div>
</div>

<style lang="scss">
  .donation-header {
    display: flex;
    justify-content: center;
    background: $donation-header-background;
    width: 100%;
    font-family: 'Helvetica Neue', Arial, sans-serif;
    font-size: $font-size-1;
    line-height: 1.3;
    color: white;
    border-bottom: 1px solid $gray-2;

    .content {
      margin: 8px 0;
      width: $content-width;
      display: flex;
      gap: 8px;
      align-items: center;

      &.expanded {
        margin: 15px 0;
        align-items: flex-start;
      }

      .collapse {
        background: transparent;
        border: 0;
        cursor: pointer;
        flex-grow: 0;
        align-self: flex-start;

        svg {
          width: 24px;
          height: 24px;
          transform: rotate(-90deg);
          margin-top: 6px;
        }

        &.expanded svg {
          transform: rotate(0);
          margin-top: 0;
        }
      }

      &.mounted {
        transition: margin 0.5s;

        .collapse svg {
          transition: transform 0.25s, margin 0.25s;
        }
      }

      .message {
        flex-grow: 1;

        &.expanded {
          padding-top: 4px;
        }

        :global(p a) {
          color: white;
        }

        .buttons {
          display: flex;
          gap: 24px;
          margin-top: 16px;
          align-items: center;

          .hide {
            background: transparent;
            border: none;
            padding: 0;
            color: white;
            font-weight: 600;
            cursor: pointer;
            font-size: $font-size-1;

            &:hover {
              text-decoration: underline;
              color: #f0f0f0;
            }
          }
        }

        .collapsed {
          display: flex;
          align-items: center;
          gap: 22px;

          .support {
            background: transparent;
            border: 0;
            padding: 0;
            font-family: 'Helvetica Neue', Arial, sans-serif;
            font-size: $font-size-1;
            font-weight: 600;
            color: white;
            cursor: pointer;
          }
        }

        .buttons .donate,
        .collapsed .donate {
          border-radius: 1000px;
          background: white;
          color: $donation-header-background;
          font-weight: 600;
          padding: 8px 24px;
          text-decoration: none;

          &:hover {
            background: #f0f0f0;
          }
        }
      }
    }
  }

  strong {
    font-weight: bold;
  }

  ul {
    margin: 16px 0;
    list-style: inside disc;
  }
</style>
